<template>
  <div class="ai-management-table">
    <rbz-table
      :columns="tableColumns"
      :table-data="tableData"
      :show-new-button="false"
      :show-download-button="false"
      :show-filter-button="true"
      :show-table-results="true"
      :show-buttons-column="false"
      :row-classes="getRowClasses"
      data-qa="ai-crawlers-table"
    >
      <template #referralVisitsHeader>
        <div class="referral-visits-header-wrapper">
          <span>{{ t('aiManagement.tableColumns.referralVisits') }}</span>
          <span
            v-b-tooltip="t('aiManagement.tableColumns.referralVisitsTooltip')"
            class="has-tooltip"
          >
            <icon-info-small color="dusty-gray" class="info-icon is-vcentered" />
          </span>
        </div>
      </template>
      <template #actionCell="cellProps">
        <span
          v-if="cellProps.row.isEnabled && cellProps.row.action !== '-'"
          class="action-badge"
          :class="getActionBadgeClass(cellProps.row)"
        >
          {{ cellProps.row.action }}
        </span>
        <span v-else>-</span>
      </template>
      <template #typeCell="cellProps">
        <span>{{ cellProps.row.isEnabled ? cellProps.row.type : '-' }}</span>
      </template>
      <template #totalRequestsCell="cellProps">
        <span>{{ cellProps.row.isEnabled ? cellProps.row.totalRequests : '-' }}</span>
      </template>
      <template #allowedToPassedCell="cellProps">
        <span>{{ cellProps.row.isEnabled ? cellProps.row.allowedToPassed : '-' }}</span>
      </template>
      <template #blockedCell="cellProps">
        <span>{{ cellProps.row.isEnabled ? cellProps.row.blocked : '-' }}</span>
      </template>
      <template #referralVisitsCell="cellProps">
        <span>{{ cellProps.row.isEnabled ? cellProps.row.referralVisits : '-' }}</span>
      </template>
      <template #actionsCell="cellProps">
        <div class="actions-wrapper">
          <button
            class="action-button toggle-button"
            :class="{ 'is-disabled': !cellProps.row.isEnabled }"
            :title="t('aiManagement.toggleEnable')"
            @click="toggleEnabled(cellProps.row)"
          >
            <icon-enable />
          </button>
          <button
            class="action-button menu-button"
            :title="t('aiManagement.moreOptions')"
            @click="openContextMenu($event, cellProps.row)"
          >
            <icon-more-vertical />
          </button>
        </div>
      </template>
    </rbz-table>

    <rbz-dropdown-menu
      dropdown-type="contextmenu"
      :context-menu-position="[contextMenuTop, contextMenuLeft]"
      ref="contextMenuRef"
    >
      <template #contextMenuContent>
        <template v-for="(item, index) in contextMenuItems" :key="item.id">
          <div
            class="context-menu-item-wrapper"
            :class="{
              'is-action': item.type === 'action',
              'is-navigation': item.type === 'navigation',
              'is-selected': item.isSelected,
            }"
          >
            <menu-list-item
              :title="item.title"
              classes="context-menu-item ignore-fix-focus"
              :data-qa="item.dataQa"
              @item-click="item.onClick"
            >
              <template v-if="item.type === 'action' && item.isSelected" #trailing-icon>
                <icon-right-tick />
              </template>
              <template v-else-if="item.type === 'navigation'" #trailing-icon>
                <icon-chevron direction="right" color="stratos" />
              </template>
            </menu-list-item>
          </div>
          <layout-divider-line v-if="index < contextMenuItems.length - 1" color="light-gray" />
        </template>
      </template>
    </rbz-dropdown-menu>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';
import RbzTable from 'ui-component-library/rbz/components/RbzTable.vue';
import RbzDropdownMenu from '@/components/RbzDropdownMenu.vue';
import MenuListItem from 'ui-component-library/rbz/components/MenuListItem.vue';
import LayoutDividerLine from 'ui-component-library/rbz/components/LayoutDividerLine.vue';
import IconEnable from 'ui-component-library/assets/icons/IconEnable.vue';
import IconMoreVertical from 'ui-component-library/assets/icons/IconMoreVertical.vue';
import IconRightTick from 'ui-component-library/assets/icons/IconRightTick.vue';
import IconChevron from 'ui-component-library/assets/icons/IconChevron.vue';
import IconInfoSmall from 'ui-component-library/assets/icons/IconInfoSmall.vue';
import { GenericObject, RbzTableColumnOptions } from 'ui-component-library/rbz/types';
import {
  AiToolStat,
  AiGlobalFilter,
  AiAction,
  CrawlerTableRow,
  SelectedContext,
  ContextMenuItemConfig,
} from '@/types';
import { AI_CRAWLERS, ACTION_TYPE_MAP, ACTION_DISPLAY_MAP, isExcludedTag } from '@/types/const';
import RequestsUtils from '@/helpers/RequestsUtils';
import Utils from 'ui-component-library/helpers/Utils';
import { useUserStore } from '@/store/UserStore';

interface Props {
  aiToolsStats: AiToolStat[];
  enabledCrawlers: Record<string, boolean>;
  globalFilters: AiGlobalFilter[];
  actions: AiAction[];
  selectedBranch: string;
}

const props = defineProps<Props>();
const emit = defineEmits(['toggle-crawler', 'refresh-filters', 'hide-others', 'show-all']);
const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const userStore = useUserStore();

const contextMenuRef = ref<InstanceType<typeof RbzDropdownMenu> | null>(null);
const contextMenuItem = ref<CrawlerTableRow | null>(null);
const contextMenuTop = ref('0px');
const contextMenuLeft = ref('0px');
const getContextMenuElement = (): HTMLElement | null => {
  const maybe = (contextMenuRef.value as unknown as { contextMenu?: unknown } | null)
    ?.contextMenu as { value?: HTMLElement | null } | HTMLElement | null | undefined;
  if (!maybe) return null;
  return (maybe as { value?: HTMLElement | null }).value ?? (maybe as HTMLElement);
};
const outsideClickListener = (event: MouseEvent) => {
  const menuElement = getContextMenuElement();
  if (menuElement?.contains(event.target as Node)) {
    return;
  }
  closeContextMenu();
};

// Intentionally disable right-click context menu on table rows:
// context menu should open only via the 3-dots button.
// const handleContextMenu = (event: MouseEvent, row: GenericObject) => {
//   openContextMenu(event, row);
// };

const createColumn = (
  id: string,
  overrides: Partial<RbzTableColumnOptions> = {}
): RbzTableColumnOptions => ({
  id,
  title: t(`aiManagement.tableColumns.${id}`),
  fieldNames: [id],
  isSortable: true,
  isSearchable: true,
  cellContentClasses: 'ellipsis',
  // onContextMenu: handleContextMenu,
  ...overrides,
});

const createNumericColumn = (
  id: string,
  overrides: Partial<RbzTableColumnOptions> = {}
): RbzTableColumnOptions =>
  createColumn(id, {
    displaySlotName: `${id}Cell`,
    isNumber: true,
    ...overrides,
  });

const tableColumns = computed((): RbzTableColumnOptions[] => [
  createColumn('aiCrawler', { autoFocus: true }),
  createColumn('action', { displaySlotName: 'actionCell' }),
  createColumn('type', { displaySlotName: 'typeCell' }),
  createNumericColumn('totalRequests'),
  createNumericColumn('allowedToPassed'),
  createNumericColumn('blocked', {
    classes: 'blocked',
    cellContentClasses: 'blocked',
  }),
  createNumericColumn('referralVisits', {
    headerSlotName: 'referralVisitsHeader',
  }),
  {
    id: 'actions',
    title: '',
    fieldNames: ['id'],
    displaySlotName: 'actionsCell',
    isSortable: false,
    isSearchable: false,
    classes: 'width-100px actions-column',
    cellContentClasses: 'actions-cell',
  },
]);

const getActionType = (actionId: string | null): string | null => {
  if (!actionId) return null;
  if (ACTION_TYPE_MAP[actionId]) return ACTION_TYPE_MAP[actionId];
  return null;
};

const getActionDisplayInfo = (
  actionId: string | null
): { text: string; badgeClass: string; type: string | null } => {
  const actionType = getActionType(actionId);
  if (!actionId) return { text: '-', badgeClass: '', type: null };

  if (!actionType) return { text: 'Custom', badgeClass: 'is-custom', type: 'custom' };

  const display = ACTION_DISPLAY_MAP[actionType] || { text: 'Custom', badgeClass: 'is-custom' };
  return { ...display, type: actionType };
};

const getCrawlerType = (tagName: string): string => {
  return AI_CRAWLERS.has(tagName) ? 'AI Crawler' : 'Search Crawler';
};

const tagToFilterMap = computed(() => {
  const map = new Map<string, AiGlobalFilter>();
  for (const filter of props.globalFilters) {
    if (filter.tags?.length) {
      for (const tag of filter.tags) {
        if (tag) {
          const trimmedTag = tag.trim();
          if (trimmedTag) {
            map.set(trimmedTag, filter);
          }
        }
      }
    }
  }
  return map;
});

const tableData = computed<CrawlerTableRow[]>(() => {
  if (!props.aiToolsStats?.length) return [];

  const filterMap = tagToFilterMap.value;

  return props.aiToolsStats
    .filter((item) => !isExcludedTag(item.tag))
    .map((item) => {
      const trimmedTag = item.tag?.trim();
      const matchedFilter = trimmedTag ? filterMap.get(trimmedTag) || null : null;
      const actionId = matchedFilter?.action || null;
      const actionInfo = getActionDisplayInfo(actionId);

      return {
        id: item.tag,
        aiCrawler: item.name,
        action: actionInfo.text,
        type: getCrawlerType(item.tag),
        totalRequests: item.total_requests || 0,
        allowedToPassed: item.total_passed || 0,
        blocked: item.total_blocked || 0,
        referralVisits: item.total_referrals || 0,
        isEnabled: props.enabledCrawlers[item.tag] !== false,
        globalFilterId: matchedFilter?.id || null,
        actionId: actionId,
        badgeClass: actionInfo.badgeClass,
        actionType: actionInfo.type,
      };
    });
});

const getActionBadgeClass = (row: GenericObject): string => {
  const typedRow = row as CrawlerTableRow;
  return typedRow.isEnabled ? typedRow.badgeClass : '';
};

const getRowClasses = (row: GenericObject): string => {
  const typedRow = row as CrawlerTableRow;
  const classes: string[] = [];
  if (props.enabledCrawlers[typedRow.id] === false) {
    classes.push('crawler-disabled');
  }
  if (contextMenuItem.value?.id === typedRow.id) {
    classes.push('context-menu-open-row');
  }
  return classes.join(' ');
};

const toggleEnabled = (row: GenericObject) => {
  const typedRow = row as CrawlerTableRow;
  emit('toggle-crawler', typedRow.id);
};

const openContextMenu = (event: MouseEvent, row: GenericObject) => {
  contextMenuItem.value = row as CrawlerTableRow;
  nextTick(() => {
    if (contextMenuRef.value) {
      contextMenuRef.value.openMenu();
      setContextMenuPosition(event.clientY, event.clientX - 10);
      document.addEventListener('mousedown', outsideClickListener, true);
    }
  });
  event.preventDefault();
  event.stopPropagation();
};

const setContextMenuPosition = (top: number, left: number) => {
  const menuHeight = 250;
  const menuWidth = 200;
  const padding = 10;
  const viewportHeight = window.innerHeight;
  const largestHeight = viewportHeight - menuHeight - padding;
  const largestWidth = window.innerWidth - menuWidth - 125;

  // If not enough space below, position above the click
  if (top > largestHeight) {
    top = top - menuHeight;
    // Ensure it doesn't go above viewport
    if (top < padding) {
      top = padding;
    }
  }

  if (left > largestWidth) {
    left = largestWidth;
  }

  contextMenuTop.value = top + 'px';
  contextMenuLeft.value = left + 'px';
};

const closeContextMenu = () => {
  if (contextMenuRef.value) {
    contextMenuRef.value.closeMenu();
  }
  document.removeEventListener('mousedown', outsideClickListener, true);
  contextMenuItem.value = null;
};

// Context Menu Model

const selectedContext = computed((): SelectedContext | null => {
  if (!contextMenuItem.value) return null;

  return {
    crawlerId: contextMenuItem.value.id,
    crawlerName: contextMenuItem.value.aiCrawler,
    filterId: contextMenuItem.value.globalFilterId,
    actionType: contextMenuItem.value.actionType,
  };
});

const currentAction = computed(() => {
  return selectedContext.value?.actionType || null;
});

const hasCrawlerFilters = computed(() => {
  const currentFilters = route.query.filters?.toString() || '';
  if (!currentFilters) return false;

  const filterParts = currentFilters.split(/, /).filter(Boolean);
  return filterParts.some((filter) => {
    const trimmed = filter.trim();
    if (!trimmed.startsWith('tags')) return false;
    return true;
  });
});

const hasEditorAccess = computed(() => {
  return userStore.checkAccessLevel(userStore.accessLevels.waapConfigEditor);
});

const handleHideOthers = () => {
  const context = selectedContext.value;
  if (context?.crawlerId) {
    emit('hide-others', context.crawlerId);
  }
  closeContextMenu();
};

const handleShowAll = () => {
  emit('show-all');
  closeContextMenu();
};

const contextMenuItems = computed((): ContextMenuItemConfig[] => {
  const items: ContextMenuItemConfig[] = [];

  // For users with editor access or higher, show all action options
  if (hasEditorAccess.value) {
    items.push(
      {
        id: 'block',
        type: 'action',
        title: t('aiManagement.contextMenu.block'),
        dataQa: 'block-button',
        actionType: 'block',
        isSelected: currentAction.value === 'block',
        onClick: () => updateAction('block'),
      },
      {
        id: 'monitor',
        type: 'action',
        title: t('aiManagement.contextMenu.monitor'),
        dataQa: 'monitor-button',
        actionType: 'monitor',
        isSelected: currentAction.value === 'monitor',
        onClick: () => updateAction('monitor'),
      }
    );
  }

  // Show "Hide Others" for all users
  items.push({
    id: 'hideOthers',
    type: 'action',
    title: t('aiManagement.contextMenu.hideOthers'),
    dataQa: 'hide-others-button',
    onClick: () => {
      handleHideOthers();
    },
  });

  // Only show "Show all" option if crawler-specific filters are applied
  if (hasCrawlerFilters.value) {
    items.push({
      id: 'showAll',
      type: 'action',
      title: t('aiManagement.contextMenu.showAll'),
      dataQa: 'show-all-button',
      onClick: () => {
        handleShowAll();
      },
    });
  }

  // For users with editor access, show "Open Filter" option
  if (hasEditorAccess.value) {
    items.push({
      id: 'openFilter',
      type: 'navigation',
      title: t('aiManagement.contextMenu.openFilter'),
      dataQa: 'open-filter-button',
      onClick: navigateToFilter,
    });
  }

  // Show "Open in Events Log" for all users
  items.push({
    id: 'openEventsLog',
    type: 'navigation',
    title: t('aiManagement.contextMenu.openEventsLog'),
    dataQa: 'open-events-log-button',
    onClick: navigateToEventsLog,
  });

  return items;
});

const updateAction = async (actionType: string) => {
  const context = selectedContext.value;

  if (!context?.filterId) {
    Utils.toast({
      message: t('aiManagement.errors.filterNotFound', {
        crawler: context?.crawlerName || 'Unknown',
      }),
      type: 'is-danger',
    });
    closeContextMenu();
    return;
  }

  const filter = props.globalFilters.find((f) => f.id === context.filterId);

  if (!filter) {
    Utils.toast({
      message: t('aiManagement.errors.filterNotFound', {
        crawler: context.crawlerName,
      }),
      type: 'is-danger',
    });
    closeContextMenu();
    return;
  }

  let actionId: string;
  if (actionType === 'block') {
    actionId = 'action-global-filter-block';
  } else if (actionType === 'monitor') {
    actionId = 'action-monitor';
  } else {
    Utils.toast({
      message: t('aiManagement.errors.invalidActionType'),
      type: 'is-danger',
    });
    closeContextMenu();
    return;
  }

  try {
    const fullFilterResponse = await RequestsUtils.sendRequest({
      methodName: 'GET',
      url: `conf/${props.selectedBranch}/global-filters/${context.filterId}`,
    });

    const filterData = fullFilterResponse.data;
    filterData.action = actionId;

    const response = await RequestsUtils.sendRequest({
      methodName: 'PUT',
      url: `conf/${props.selectedBranch}/global-filters/${context.filterId}`,
      data: filterData,
      successMessage: t('aiManagement.messages.actionUpdated', {
        crawler: context.crawlerName,
        action: actionType,
      }),
      failureMessage: t('aiManagement.messages.actionUpdateFailed'),
    });

    if (response) {
      emit('refresh-filters');
    }
  } catch (error) {
    console.error('Error updating action:', error);
  }

  closeContextMenu();
};

const navigateToFilter = () => {
  const context = selectedContext.value;

  if (!context?.filterId) {
    Utils.toast({
      message: t('aiManagement.errors.filterNotFound', {
        crawler: context?.crawlerName || 'Unknown',
      }),
      type: 'is-danger',
    });
    closeContextMenu();
    return;
  }

  router.push(`/${props.selectedBranch}/global-filters/config/${context.filterId}`);
  closeContextMenu();
};

const extractTimestampFromFilters = (filters: string): string | null => {
  if (!filters) return null;

  const filterParts = filters.split(/, /);
  const timestampFilter = filterParts.find((filter) => filter.trim().startsWith('timestamp'));

  return timestampFilter || null;
};

const navigateToEventsLog = () => {
  const context = selectedContext.value;
  if (!context) return;

  const filters: string[] = [];

  // Add the specific crawler tag
  filters.push(`tags="${context.crawlerId}"`);

  // Add ai-dashboard tag for consistency with AI Management
  filters.push('tags="ai-dashboard"');

  // Add timestamp filter if present
  const currentFilters = route.query.filters?.toString() || '';
  const timestampFilter = extractTimestampFromFilters(currentFilters);
  if (timestampFilter) {
    filters.push(timestampFilter);
  }

  router.push({
    path: `/${props.selectedBranch}/events-log`,
    query: { filters: filters.join(', ') },
  });
  closeContextMenu();
};
</script>

<style scoped lang="scss">
@use 'sass:map';
@use 'ui-component-library/assets/styles/colors' as colors;

.ai-management-table :deep(.blocked) {
  color: map.get(colors.$colors, 'punch');
}

.action-badge {
  display: inline-block;
  height: 24px;
  padding: 4px 8px;
  color: map.get(colors.$colors, 'stratos');
  border-radius: 4px;
  text-align: center;

  &.is-blocked {
    background-color: map.get(colors.$colors, 'tabasco');
    color: map.get(colors.$colors, 'link-water');
  }

  &.is-monitor {
    background-color: map.get(colors.$colors, 'jewel');
    color: map.get(colors.$colors, 'link-water');
  }

  &.is-custom {
    background-color: map.get(colors.$colors, 'neon-carrot');
    color: map.get(colors.$colors, 'stratos');
  }
}

.ai-management-table :deep(.actions-column) {
  text-align: center;
}

.ai-management-table :deep(.actions-cell) {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 4px 8px;
}

.actions-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.action-button {
  background: none;
  border: 0;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.7;
  }

  svg {
    width: 20px;
    height: 20px;
    fill: map.get(colors.$colors, 'stratos');
  }
}

.toggle-button.is-disabled {
  svg {
    opacity: 0.2;
  }
}

.ai-management-table :deep(.data-row) {
  min-height: 54px;
}

.ai-management-table :deep(.data-row.crawler-disabled) {
  opacity: 0.55;
}

.ai-management-table :deep(.data-row.context-menu-open-row) {
  background-color: map.get(colors.$colors, 'hawkes-blue');
}

.context-menu-item {
  cursor: pointer;
}

.context-menu-item-wrapper {
  display: flex;
  align-items: center;
}

.context-menu-item-wrapper.is-selected :deep(.menu-list-item) {
  font-weight: 600;
  color: map.get(colors.$colors, 'jewel');
}

.context-menu-item-wrapper.is-navigation :deep(.menu-list-item) {
  background-color: map.get(colors.$colors, 'gallery');

  &:hover {
    background-color: map.get(colors.$colors, 'white-ice');
  }

  &:active {
    background-color: map.get(colors.$colors, 'white-ice');
    color: map.get(colors.$colors, 'stratos');
  }
}

.ai-management-table :deep(.context-menu-content) {
  padding: 0;
}

.ai-management-table :deep(.menu-list-item:focus:not(:active)) {
  box-shadow: none;
}

.referral-visits-header-wrapper {
  display: flex;
  align-items: center;
  gap: 4px;

  .has-tooltip {
    display: inline-flex;
    align-items: center;
    cursor: help;
  }

  .info-icon {
    width: 16px;
    height: 16px;
  }
}
</style>
